import type { ApolloClient } from '@apollo/client';
import type {
  RoleSearchByCommunityQuery,
  RoleSearchByCommunityQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import AsyncSearchableSelectFieldEntry from '../../components/form/AsyncSearchableSelectField/AsyncSearchableSelectFieldEntry';
import roleSearchQuery from '../../components/users/Roles/RoleSearchByCommunity.query.graphql';

const log = getLog(module);

/**
 * Refetch roles view query based on the input value to match with role name
 * @param client the apollo client
 * @param inputValue the value typed to match as community role
 * @param rolesToExclude value to filter out from the final roles result list
 * @param featureId value to fetch roles for the specified feature ID.
 * @param minInputLengthForSearch the minimum input length for search
 * @param afterCursor the after cursor
 * @param usePagination Whether to get paginated response or not
 * @param useRolePolicyCheck whether to check policy to filter roles or not
 */
export default async function fetchRolesListBySearchTerm(
  client: ApolloClient<unknown>,
  inputValue: string,
  rolesToExclude: string[] = [],
  featureId?: string,
  minInputLengthForSearch = 0,
  afterCursor?: string,
  usePagination = false,
  useRolePolicyCheck = false
): Promise<{
  options: AsyncSearchableSelectFieldEntry<{ id: string; name: string }>[];
  hasMore: boolean;
  cursor: string;
}> {
  if (inputValue.length >= minInputLengthForSearch) {
    const { loading, data, errors } = await client.query<
      RoleSearchByCommunityQuery,
      RoleSearchByCommunityQueryVariables
    >({
      query: roleSearchQuery,
      variables: {
        constraints: {
          featureId: {
            ...(featureId && { eq: featureId })
          },
          searchTerm: {
            ...(inputValue && { eq: inputValue.toLowerCase() })
          }
        },
        ...(usePagination && { first: 10, after: afterCursor }),
        useFullPageInfo: usePagination,
        useRolePolicyCheck: useRolePolicyCheck
      },
      fetchPolicy: 'network-only'
    });

    if (errors) {
      log.error(errors, 'An error occurred while loading roles');
    }

    const roles = loading
      ? []
      : data?.community.roles.edges
          .filter(role => {
            const rolePolicyCheck = useRolePolicyCheck
              ? checkPolicy(role.node.rolePolicies.canAssignRemoveRole)
              : true;
            return !rolesToExclude.includes(role.node.name) && rolePolicyCheck;
          })
          .map(
            role =>
              new AsyncSearchableSelectFieldEntry<{ id: string; name: string }>(
                role.node.name,
                role.node.name,
                { id: role.node.id, name: role.node.name }
              )
          );

    const endCursor = data?.community.roles?.pageInfo?.endCursor;
    const hasNextPage = data?.community.roles?.pageInfo?.hasNextPage;

    return {
      options: roles,
      hasMore: usePagination ? hasNextPage : false,
      cursor: usePagination ? endCursor : null
    };
  }
  return {
    options: [],
    hasMore: false,
    cursor: null
  };
}
